// P14 — Modal de detalhes da cota (abre ao clicar no QuotaIndicator).
//
// Mostra progress bars dos limites de tokens, lista de modelos disponíveis,
// outros limites (agentes/arquivos/MB), última mudança feita pelo admin (Q6)
// e CTA "Pedir mais ao admin".

import { FC, useState } from 'react';
import { MyQuota } from '@/types/agents';
import QuotaIncreaseRequestModal from './QuotaIncreaseRequestModal';

interface Props {
  quota: MyQuota;
  onClose: () => void;
  onRefresh?: () => void;
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function ProgressBar({
  label,
  used,
  limit,
}: {
  label: string;
  used: number;
  limit: number;
}) {
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
  const danger = pct >= 90;
  const warn = pct >= 70 && pct < 90;
  const color = danger ? 'bg-red-400' : warn ? 'bg-amber-400' : 'bg-accent';
  const textColor = danger
    ? 'text-red-300'
    : warn
      ? 'text-amber-300'
      : 'text-content-primary';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-content-tertiary">{label}</span>
        <span className={`font-mono ${textColor}`}>
          {used.toLocaleString('pt-BR')} / {limit.toLocaleString('pt-BR')} (
          {Math.round(pct)}%)
        </span>
      </div>
      <div className="h-2 rounded-full bg-surface-border overflow-hidden">
        <div className={`h-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const QuotaDetailsModal: FC<Props> = ({ quota, onClose, onRefresh }) => {
  const [showRequest, setShowRequest] = useState(false);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="quota-details-modal"
    >
      <div
        className="bg-surface-primary border border-surface-border rounded-xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-content-primary">Sua cota de IA</h2>

        <section className="space-y-3">
          <ProgressBar
            label="Tokens hoje"
            used={quota.tokens_used_today}
            limit={quota.max_tokens_per_day}
          />
          <ProgressBar
            label="Tokens este mês"
            used={quota.tokens_used_this_month}
            limit={quota.max_tokens_per_month}
          />
        </section>

        <section>
          <h3 className="text-sm font-semibold text-content-primary mb-1">
            Modelos disponíveis pra você
          </h3>
          {quota.allowed_models.length === 0 ? (
            <p className="text-xs text-content-tertiary">Nenhum modelo liberado.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="qdm-allowed-models">
              {quota.allowed_models.map((m) => {
                const label = quota.available_models_catalog?.[m]?.label ?? m;
                return (
                  <span
                    key={m}
                    className="bg-surface-secondary border border-surface-border px-2 py-0.5 rounded text-xs text-content-primary"
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <h3 className="text-sm font-semibold text-content-primary mb-1">Outros limites</h3>
          <ul className="text-sm space-y-0.5 text-content-tertiary">
            <li>
              Agentes pessoais: máx{' '}
              <strong className="text-content-primary">{quota.max_personal_agents}</strong>
            </li>
            <li>
              Arquivos por agente: máx{' '}
              <strong className="text-content-primary">{quota.max_files_per_agent}</strong>
            </li>
            <li>
              Tamanho de arquivo: máx{' '}
              <strong className="text-content-primary">{quota.max_file_size_mb} MB</strong>
            </li>
          </ul>
        </section>

        {quota.last_change && (
          <section
            className="text-xs text-content-primary bg-blue-500/10 border border-blue-500/30 rounded-lg p-2"
            data-testid="qdm-last-change"
          >
            <p>
              <span className="text-content-tertiary">Última mudança: </span>
              <code className="bg-surface-secondary px-1 rounded">
                {quota.last_change.field_changed}
              </code>{' '}
              → <code className="bg-surface-secondary px-1 rounded">{String(quota.last_change.new_value)}</code>
            </p>
            <p className="mt-0.5 text-content-tertiary">
              {fmtDate(quota.last_change.created_at)}
              {quota.last_change.admin_user_name && (
                <> por {quota.last_change.admin_user_name}</>
              )}
              {quota.last_change.reason && <> · "{quota.last_change.reason}"</>}
            </p>
          </section>
        )}

        <div className="flex gap-2 pt-3 border-t border-surface-border">
          <button
            type="button"
            onClick={onClose}
            data-testid="qdm-close"
            className="rounded-lg border border-surface-border px-4 py-2 text-sm text-content-primary"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={() => setShowRequest(true)}
            data-testid="qdm-request"
            className="flex-1 rounded-lg bg-accent text-surface-primary font-semibold px-4 py-2 text-sm"
          >
            📨 Pedir mais ao admin
          </button>
        </div>
      </div>

      {showRequest && (
        <QuotaIncreaseRequestModal
          quota={quota}
          onClose={() => setShowRequest(false)}
          onSubmitted={() => {
            setShowRequest(false);
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
};

export default QuotaDetailsModal;
